/* eslint-disable react/no-unstable-nested-components */
import { useQuery } from "@apollo/client";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import TablePagination from "components/ui/Table/TablePagination";
import classNames from "components/ui/classNames";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import subYears from "date-fns/subYears";
import CvmMaterialFactsQuery from "lib/queries/CvmMaterialFactsQuery";
import type {
  ICvmMaterialFactsQuery,
  ICvmMaterialFactsQueryVariables,
} from "lib/queries/__generated__/CvmMaterialFactsQuery.generated";
import { FormattedMessage, useIntl } from "react-intl";

type CvmMaterialFact = ICvmMaterialFactsQuery["cvmMaterialFacts"]["nodes"][0];

interface BrazilianIpesContentProps {
  isRenderPageDefault: boolean;
}

export default function BrazilianIpesContent({ isRenderPageDefault }: BrazilianIpesContentProps) {
  const intl = useIntl();
  const issuer = useBrazilianIssuer();
  const columnHelper = createColumnHelper<CvmMaterialFact>();

  const { data, loading } = useQuery<ICvmMaterialFactsQuery, ICvmMaterialFactsQueryVariables>(CvmMaterialFactsQuery, {
    variables: {
      filterByIssuerCnpj: issuer.cnpj,
      deliveryDateStart: lightFormat(subYears(new Date(), 1), "yyyy-MM-dd"),
      deliveryDateEnd: lightFormat(new Date(), "yyyy-MM-dd"),
      filterByCategories: null,
      filterByIssuers: null,
      limit: 30,
      cursor: null,
    },
  });

  const columns = [
    columnHelper.accessor("category", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-left text-sm font-semibold text-slate-600 dark:text-slate-300">
          <FormattedMessage defaultMessage="Tipo" id="wt+XcA" />
        </div>
      ),
      cell: (info) => (
        <div className="p-4">
          <a
            href={info.row.original.url}
            rel="noopener noreferrer"
            className="underline-offset-2 hover:underline"
            target="_blank"
          >
            <div className="flex items-center space-x-2">
              {info.getValue() === "Fato Relevante" && (
                <div className="flex size-2 items-center justify-center rounded-lg bg-red-600" />
              )}
              {info.getValue() === "Aviso aos Acionistas" && info.row.original.type === "Outros avisos" && (
                <div className="flex size-2 items-center justify-center rounded-lg bg-yellow-600" />
              )}
              {info.getValue() === "Dados Econômico-Financeiros" && (
                <div className="flex size-2 items-center justify-center rounded-lg bg-blue-600" />
              )}
              {info.getValue() === "Comunicado ao Mercado" && (
                <div className="flex size-2 items-center justify-center rounded-lg bg-green-800" />
              )}

              {info.row.original.subjects.length > 0 && (
                <span
                  className={classNames(
                    "max-w-sm truncate whitespace-nowrap",
                    "text-sm font-medium text-neutral-700 dark:text-slate-300",
                  )}
                >
                  {info.row.original.subjects[0]}
                </span>
              )}
            </div>
          </a>
          <span className="flex text-sm text-slate-600 dark:text-slate-400">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor("deliveryDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
          <FormattedMessage defaultMessage="Data" id="XHHR08" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames("whitespace-nowrap px-4 py-1.5 text-sm font-normal text-slate-600 dark:text-slate-400")}
        >
          <span className="text-sm text-slate-600 dark:text-slate-400">
            {intl.formatDate(parseISO(info.getValue()), {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })}
          </span>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: data?.cvmMaterialFacts.nodes || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: isRenderPageDefault ? 5 : 10,
      },
    },
  });

  if (loading) {
    return (
      <div className="flex h-full w-full flex-col">
        <LoadingSkeleton count={1} className="h-full min-h-96" />
      </div>
    );
  }

  if (!data || data.cvmMaterialFacts.nodes.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-slate-400">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className={classNames("lg:w-full", {
          "pb-4": table.getPageCount() > 1,
        })}
      >
        <div className={classNames("relative overflow-auto rounded-xl border border-slate-200 dark:border-slate-950")}>
          <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                >
                  {headerGroup.headers
                    .filter((header) => !header.column.columnDef.meta?.isHidden)
                    .map((header) => {
                      return (
                        <th
                          key={header.id}
                          className={classNames(
                            "m-0 bg-slate-50 p-0 text-center dark:bg-slate-800",
                            header.column.columnDef.meta?.header?.className,
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      );
                    })}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                    {row
                      .getVisibleCells()
                      .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                      .map((cell) => {
                        return (
                          <td
                            key={cell.id}
                            className={classNames("m-0 p-0 text-center", cell.column.columnDef.meta?.cell?.className)}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end">
        <TablePagination<CvmMaterialFact> table={table} />
      </div>
    </div>
  );
}
